import { useProductFlashSale } from '@/hooks/react-query/flash-sale/use-flash-sale'
import { useCoolClubPrice } from '@/hooks/use-coolclub-price'
import { useProductDetail } from '@/hooks/zustands/use-product-detail'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { getValue, stringToSlug } from '@/lib/utils'
import { BreadCrumbItem, Option } from '@/types/product'
import dayjs from 'dayjs'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useProductDetailSafe } from '../context/product-detail-context'

export const useProductDetailStore = () => {
  const {
    product,
    colorSelected,
    setColorSelected,
    setColorOptionActive,
    sizeSelected,
    setSizeSelected,
    sizeOptions,
    sortedColorOptions,
    comboSelectedInfo,
    setComboSelectedInfo,
  } = useProductDetailSafe()

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  // State for variants UI
  const [isOpen, setIsOpen] = useState(false)
  const [colorsPerRow, setColorsPerRow] = useState(5)

  /**
   * Size selection logic
   */
  const isDisableSize = useCallback(
    (size: string) => {
      if (!colorSelected || !product.mapped_variants) {
        return true
      }

      const colorKey = Object.keys(product.mapped_variants).find(
        (key) => stringToSlug(key) === colorSelected
      )

      if (!colorKey) {
        return true
      }

      const variantsOfCurrentColor = product.mapped_variants[colorKey]

      const variantWithSize = variantsOfCurrentColor.find(
        (variant) => variant.size === size
      )

      if (!variantWithSize || variantWithSize.isDisable) {
        return true
      }

      return false
    },
    [colorSelected, product.mapped_variants]
  )

  // Auto-select first valid size when color changes
  const updateSizeOnColorChange = useCallback(() => {
    if (!colorSelected || !product.mapped_variants) return

    const isSizeValid = sizeSelected && !isDisableSize(sizeSelected)

    if (!isSizeValid) {
      const firstValidSize = sizeOptions.find((size) => !isDisableSize(size))
      if (firstValidSize) {
        setSizeSelected(firstValidSize)
      }
    }
  }, [
    colorSelected,
    sizeSelected,
    setSizeSelected,
    sizeOptions,
    isDisableSize,
    product.mapped_variants,
  ])

  useEffect(() => {
    updateSizeOnColorChange()
  }, [updateSizeOnColorChange])

  /**
   * Combo size selection logic
   */
  const isDisableComboSize = useCallback(
    (size: string, index: number) => {
      // For combo with only size1 and size2
      const sizeKey = index === 0 ? 'option2' : 'option1'
      const pairedSize = getValue(comboSelectedInfo, sizeKey, '')

      const variant = product.combo?.variants.find((v) =>
        index === 0
          ? v.option1 === size && v.option2 === pairedSize
          : v.option1 === pairedSize && v.option2 === size
      )

      return !variant || variant.isDisable
    },
    [product.combo?.variants, comboSelectedInfo]
  )

  const handleComboSizeChange = useCallback(
    (value: string, optionIndex: number) => {
      if (!isDisableComboSize(value, optionIndex)) {
        setComboSelectedInfo({
          ...comboSelectedInfo,
          [`option${optionIndex + 1}`]: value,
        })
      }
    },
    [comboSelectedInfo, isDisableComboSize, setComboSelectedInfo]
  )

  /**
   * Color selection logic
   */
  const handleChangeColor = useCallback(
    (variant: Option) => {
      const slug = stringToSlug(variant.title)
      setColorSelected(slug)
      setColorOptionActive(variant)
      const params = new URLSearchParams(searchParams)
      params.set('color', slug)
      router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    },
    [router, pathname, setColorSelected, setColorOptionActive, searchParams]
  )

  // Initialize color from URL or first color
  const initializeColorFromURL = useCallback(() => {
    const colorFromURL = searchParams.get('color')

    const matched = sortedColorOptions.find(
      (option: Option) => stringToSlug(option.title) === colorFromURL
    )

    if (colorFromURL && matched) {
      // If there's a color from URL and it matches, use it
      setColorSelected(colorFromURL)
      setColorOptionActive(matched)
    } else if (sortedColorOptions.length > 0) {
      // If color from URL doesn't match, or there's no color from URL → fallback to first color
      const firstColor = sortedColorOptions[0]
      const slug = stringToSlug(firstColor.title)

      setColorSelected(slug)
      setColorOptionActive(firstColor)

      // Update URL if not correct
      if (colorFromURL !== slug) {
        const params = new URLSearchParams(searchParams)
        params.set('color', slug)
        router.replace(`${pathname}?${params.toString()}`, { scroll: false })
      }
    }
  }, [
    searchParams,
    sortedColorOptions,
    setColorSelected,
    setColorOptionActive,
    router,
    pathname,
  ])

  /**
   * Tracking logic
   */
  function convertBreadcrumbToCategory(bread_crumb: BreadCrumbItem[]) {
    return bread_crumb.reduce(
      (acc, item, index) => {
        const key = index === 0 ? 'item_category' : `item_category${index + 1}`
        acc[key] = item.title
        return acc
      },
      {} as Record<string, string>
    )
  }

  const handleTracking = useCallback(() => {
    const items = product?.variants.map((variant) => {
      return {
        item_id: variant.id,
        item_name: product.title,
        price: variant.regular_price,
        quantity: variant.quantity,
        discount: product.sale_percent,
        item_brand: 'Coolmate',
      }
    })

    const data = {
      event: ETrackingEvent.ViewItem,
      ecommerce: {
        product_id: product.id,
        product_name: product.title,
        product_price: product.regular_price,
        product_quantity: 1,
        product_url: product.seo_alias,
        product_image: product.default_thumbnail,
        product_variant: colorSelected,
        product_discount: product.sale_percent,
        product_rating: product.review.ratingValue,
        ...convertBreadcrumbToCategory(product.bread_crumb),
        items: items,
      },
    }

    trackEvent(data)
  }, [product, colorSelected, trackEvent])

  // Fallback and handle when colorSelected changes
  const handleColorSelectedChange = useCallback(() => {
    if (!colorSelected) return

    const matched = sortedColorOptions.find(
      (option: Option) => stringToSlug(option.title) === colorSelected
    )

    if (matched) {
      setColorOptionActive(matched)
    } else if (sortedColorOptions.length > 0) {
      // If no matching color is found, use the first color
      const fallback = sortedColorOptions[0]
      setColorSelected(stringToSlug(fallback.title))
      setColorOptionActive(fallback)
    }
    handleTracking()
  }, [
    colorSelected,
    sortedColorOptions,
    setColorOptionActive,
    setColorSelected,
    handleTracking,
  ])

  // Initialize color from URL on component mount
  useEffect(() => {
    initializeColorFromURL()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, product, pathname, router, sortedColorOptions])

  // Handle color selection changes
  useEffect(() => {
    handleColorSelectedChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorSelected, sortedColorOptions])

  /**
   * Quantity adjustment logic
   */
  const { quantity, setQuantity, handleAddToCart, isPendingAddToCart } =
    useProductDetailSafe()

  const handleQuantityChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // Remove any non-digit characters
      const value = e.target.value.replace(/\D/g, '')

      // Prevent leading zeros
      if (value.length > 1 && value[0] === '0') {
        setQuantity(parseInt(value.slice(1)))
        return
      }

      // Don't allow empty value
      if (!value) {
        setQuantity(1)
        return
      }

      // Convert to number and validate range
      const numValue = parseInt(value)
      if (numValue > 999) {
        setQuantity(999)
      } else {
        setQuantity(numValue)
      }
    },
    [setQuantity]
  )

  const handleIncrement = useCallback(() => {
    if (quantity < 999) {
      setQuantity(quantity + 1)
    }
  }, [quantity, setQuantity])

  const handleDecrement = useCallback(() => {
    if (quantity > 1) {
      setQuantity(quantity - 1)
    }
  }, [quantity, setQuantity])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleIncrement()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleDecrement()
      }
    },
    [handleIncrement, handleDecrement]
  )

  /**
   * Flash Sale and product pricing logic
   */
  const productFlashSale = useProductFlashSale(product.id)

  // Kiểm tra xem có Flash Sale không, phải nằm trong khoảng thời gian Flash Sale so với hiện tại
  const isFlashSale =
    productFlashSale?.endsAt &&
    productFlashSale?.product &&
    dayjs(productFlashSale?.endsAt).isAfter(dayjs()) &&
    dayjs(productFlashSale?.startsAt).isBefore(dayjs())

  // Xử lý giá sản phẩm với hook useCoolClubPrice
  // Nếu có Flash Sale, xử lý giá Flash Sale trước
  const { productData: baseProductData, isCoolClubSale } =
    useCoolClubPrice(product)

  // Kết hợp giá Flash Sale nếu có
  const productData = useMemo(() => {
    return isFlashSale
      ? {
          ...baseProductData,
          regular_price: productFlashSale.product.regular_price,
          compare_price: productFlashSale.product.compare_price,
          sale_percent: productFlashSale.product.compare_price
            ? 100 -
              Math.round(
                (productFlashSale.product.regular_price /
                  productFlashSale.product.compare_price) *
                  100
              )
            : 0,
        }
      : baseProductData
  }, [isFlashSale, baseProductData, productFlashSale])

  /**
   * Add to cart logic
   */
  const submitAddToCart = useCallback(
    (payload: {
      regularPrice: number
      comparePrice: number
      giftId?: string
      giftOptionId?: string
    }) => {
      handleAddToCart(payload)
    },
    [handleAddToCart]
  )

  // Get gift data from product detail context
  const { giftData } = useProductDetail()

  // Shared handleAddToCartClick function for both components
  const handleAddToCartClick = useCallback(
    (options?: { skipPendingCheck?: boolean }) => {
      // Check if add to cart is already in progress
      if (!options?.skipPendingCheck && isPendingAddToCart) return

      // Use prices from productData
      submitAddToCart({
        regularPrice: productData.regular_price,
        comparePrice: productData.compare_price || 0,
        giftId: giftData?.id || '',
        giftOptionId: giftData?.optionId || '',
      })
    },
    [isPendingAddToCart, submitAddToCart, giftData, productData]
  )

  return {
    // Size logic
    isDisableSize,
    updateSizeOnColorChange,

    // Combo logic
    isDisableComboSize,
    handleComboSizeChange,

    // Color logic
    handleChangeColor,
    initializeColorFromURL,
    handleColorSelectedChange,

    // UI state for variants
    isOpen,
    setIsOpen,
    colorsPerRow,
    setColorsPerRow,

    // Quantity logic
    quantity,
    handleQuantityChange,
    handleIncrement,
    handleDecrement,
    handleKeyDown,

    // Add to cart logic
    submitAddToCart,
    isPendingAddToCart,
    handleAddToCartClick,

    // Flash Sale and pricing logic
    isFlashSale,
    productData,
    isCoolClubSale,
    productFlashSale,

    // Tracking
    handleTracking,
    convertBreadcrumbToCategory,
  }
}
